import { useState } from 'react';
import Dropdown from 'antd/es/dropdown';
import type { MenuProps } from 'antd/es/menu';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { useLanguage } from '@/entities/locale';
import { buildStaticAssetUrl, deviceSizes } from '@/shared/config';
import { SUPPORTED_LOCALES, type SupportedLocale } from '@/shared/i18n';

function buildFlagUrl(locale: SupportedLocale) {
  return buildStaticAssetUrl('images', `translations/${locale}.png`);
}

const LanguageTrigger = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  color: var(--white);
  cursor: pointer;
  display: inline-flex;
  gap: 5px;
  font-family: var(--font-sans);
  font-size: 14px;
  font-weight: var(--weight-semibold);
  justify-content: center;
  min-block-size: 44px;
  min-inline-size: 44px;

  &:active {
    opacity: 0.8;
  }
  padding: 4px 2px;
`;

const TriggerLabel = styled.span`
  @media screen and (max-width: ${deviceSizes.xs}) {
    display: none;
  }
`;

const FlagIcon = styled.img`
  border-radius: 2px;
  flex-shrink: 0;
  height: 15px;
  width: 21px;
  object-fit: cover;
`;

const Chevron = styled.span<{ $open: boolean }>`
  font-size: 10px;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'none')};
  transition: transform var(--duration-fast) ease;
`;

const MenuItemLabel = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
`;

const MenuItemLabelStart = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
`;

function LanguageLabel({ locale, active }: { locale: SupportedLocale; active: boolean }) {
  const intl = useIntl();

  return (
    <MenuItemLabel>
      <MenuItemLabelStart>
        <FlagIcon alt={`${locale} flag`} src={buildFlagUrl(locale)} />
        {intl.formatMessage({ id: `DashBoardHeader.language.${locale}` })}
      </MenuItemLabelStart>
      {active ? (
        <svg
          aria-hidden="true"
          fill="none"
          height="15"
          stroke="var(--green-600)"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2.4"
          viewBox="0 0 24 24"
          width="15"
        >
          <path d="M20 6L9 17l-5-5" />
        </svg>
      ) : null}
    </MenuItemLabel>
  );
}

export function LanguageBox() {
  const { lang, changeLang } = useLanguage();
  const intl = useIntl();
  const [open, setOpen] = useState(false);

  const items: MenuProps['items'] = SUPPORTED_LOCALES.map((locale) => ({
    key: locale,
    label: <LanguageLabel active={locale === lang} locale={locale} />,
  }));

  return (
    <Dropdown
      menu={{
        items,
        onClick: ({ key }) => changeLang(key as SupportedLocale),
        selectedKeys: [lang],
        selectable: true,
      }}
      onOpenChange={setOpen}
      open={open}
      placement="bottomRight"
      trigger={['click']}
    >
      <LanguageTrigger aria-haspopup="listbox" aria-label="Change language" title="Change language" type="button">
        <FlagIcon alt={`${lang} flag`} src={buildFlagUrl(lang)} />
        <TriggerLabel>{intl.formatMessage({ id: `DashBoardHeader.language.${lang}` })}</TriggerLabel>
        <Chevron $open={open}>▾</Chevron>
      </LanguageTrigger>
    </Dropdown>
  );
}
